import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { CalloutButton } from './CalloutButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ECOSYSTEM_URL_PATTERN = /^https:\/\/ecosystem\.hubspot(qa)?\.com\/marketplace(.*)$/;
function CollectionLink({
  href,
  children
}) {
  const match = href.match(ECOSYSTEM_URL_PATTERN);
  if (match) {
    const parsed = new URL(href);
    const hasUtmParams = Array.from(parsed.searchParams.keys()).some(param => param.startsWith('utm'));
    if (!hasUtmParams) {
      const routerPath = parsed.pathname.replace('/marketplace', '') || '/';
      return /*#__PURE__*/_jsx(UIRouterLink, {
        to: routerPath,
        children: children
      });
    }
  }
  return /*#__PURE__*/_jsx(Link, {
    href: href,
    children: children
  });
}
const BUBBLE_SIZE = '28px';
const ICON_IMG_SIZE = '16px';
const PREVIEW_LIMIT_COMPACT = 3;
const PREVIEW_LIMIT_WIDE = 5;
const GRID_ICON_SIZE = '36px';
// --- Compact layout (half-width slots): overlapping circle icons ---

function IconBubble({
  children,
  first
}) {
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-default",
    borderRadius: "full",
    padding: "25",
    marginInlineStart: first ? '0' : '-200',
    children: /*#__PURE__*/_jsx(Flex, {
      inlineSize: BUBBLE_SIZE,
      blockSize: BUBBLE_SIZE,
      borderRadius: "full",
      backgroundColor: "fill-accent-neutral-subtle-alt",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      children: children
    })
  });
}
function CompactPreviewIcon({
  item,
  first
}) {
  const bubble = /*#__PURE__*/_jsx(IconBubble, {
    first: first,
    children: item.iconUrl ? /*#__PURE__*/_jsx(Image, {
      src: item.iconUrl,
      alt: item.name,
      inlineSize: ICON_IMG_SIZE,
      blockSize: ICON_IMG_SIZE
    }) : null
  });
  return /*#__PURE__*/_jsx(Tooltip, {
    title: item.name,
    children: item.listingUrl ? /*#__PURE__*/_jsx(CollectionLink, {
      href: item.listingUrl,
      children: bubble
    }) : /*#__PURE__*/_jsx(View, {
      tabIndex: 0,
      children: bubble
    })
  });
}
function CompactPreview({
  items,
  remaining
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [items.map((item, idx) => /*#__PURE__*/_jsx(CompactPreviewIcon, {
      item: item,
      first: idx === 0
    }, item.listingId)), remaining > 0 && /*#__PURE__*/_jsx(View, {
      backgroundColor: "fill-surface-default",
      borderRadius: "full",
      padding: "25",
      marginInlineStart: "-200",
      children: /*#__PURE__*/_jsx(Flex, {
        backgroundColor: "fill-accent-neutral-subtle-alt",
        borderRadius: "full",
        paddingInline: "200",
        blockSize: BUBBLE_SIZE,
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/_jsxs(Text, {
          typography: "detail-100",
          color: "text-core-subtle",
          children: ["+", remaining]
        })
      })
    })]
  });
}

// --- Wide layout (full-width slots): 3×2 grid with icon + name ---

function GridCell({
  item
}) {
  const content = /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    gap: "300",
    children: [/*#__PURE__*/_jsx(Flex, {
      inlineSize: GRID_ICON_SIZE,
      blockSize: GRID_ICON_SIZE,
      borderRadius: "200",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      flexShrink: 0,
      children: item.iconUrl ? /*#__PURE__*/_jsx(Image, {
        src: item.iconUrl,
        alt: item.name,
        inlineSize: GRID_ICON_SIZE,
        blockSize: GRID_ICON_SIZE
      }) : null
    }), /*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      color: "text-core-default",
      children: item.name
    })]
  });
  return item.listingUrl ? /*#__PURE__*/_jsx(CollectionLink, {
    href: item.listingUrl,
    children: content
  }) : content;
}
function ListingGrid({
  items,
  remaining,
  formatMoreCount
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    flexWrap: "wrap",
    gap: "400",
    alignItems: "center",
    children: [items.map(item => /*#__PURE__*/_jsx(View, {
      inlineSize: "calc(50% - 8px)",
      flexShrink: 0,
      children: /*#__PURE__*/_jsx(GridCell, {
        item: item
      })
    }, item.listingId)), remaining > 0 && /*#__PURE__*/_jsx(View, {
      inlineSize: items.length % 2 === 0 ? '100%' : 'calc(50% - 8px)',
      flexShrink: 0,
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        blockSize: GRID_ICON_SIZE,
        justifyContent: "start",
        children: /*#__PURE__*/_jsx(View, {
          borderWidth: "100",
          borderStyle: "solid",
          borderColor: "border-core-default",
          borderRadius: "full",
          paddingInline: "400",
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            color: "text-core-subtle",
            children: formatMoreCount ? formatMoreCount(remaining) : `+${remaining}`
          })
        })
      })
    })]
  });
}

// --- Main component ---

export function CollectionCard({
  title,
  description,
  buttonText,
  buttonUrl,
  previewItems,
  listingCount,
  sectionWidth,
  onClick,
  openCopilot,
  CtaButton = CalloutButton,
  formatMoreCount
}) {
  const isWide = sectionWidth === 'FULL' || sectionWidth === 'TWO_THIRDS';
  const limit = isWide ? PREVIEW_LIMIT_WIDE : PREVIEW_LIMIT_COMPACT;
  const items = previewItems.slice(0, limit);
  const remaining = Math.max(0, listingCount - items.length);
  if (isWide) {
    return /*#__PURE__*/_jsx(CardWrapper, {
      margin: "0",
      overflow: "hidden",
      children: /*#__PURE__*/_jsx(CardSection, {
        padding: "0",
        margin: "0",
        children: /*#__PURE__*/_jsxs(Flex, {
          gap: "600",
          padding: "600",
          children: [/*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            gap: "400",
            flex: "1",
            minInlineSize: "0",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "heading-200",
              color: "text-core-default",
              children: title
            }), description && /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              color: "text-core-subtle",
              children: description
            }), /*#__PURE__*/_jsx(View, {
              marginBlockStart: "200",
              children: /*#__PURE__*/_jsx(CtaButton, {
                buttonText: buttonText,
                buttonUrl: buttonUrl,
                onClick: onClick,
                openCopilot: openCopilot,
                use: "secondary"
              })
            })]
          }), items.length > 0 && /*#__PURE__*/_jsx(Flex, {
            flex: "1",
            alignItems: "center",
            minInlineSize: "0",
            children: /*#__PURE__*/_jsx(ListingGrid, {
              items: items,
              remaining: remaining,
              formatMoreCount: formatMoreCount
            })
          })]
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(CardWrapper, {
    margin: "0",
    overflow: "hidden",
    blockSize: "100%",
    children: /*#__PURE__*/_jsx(CardSection, {
      padding: "0",
      margin: "0",
      blockSize: "100%",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "400",
        blockSize: "100%",
        padding: "600",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-200",
          color: "text-core-default",
          children: title
        }), description && /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          color: "text-core-subtle",
          children: description
        }), /*#__PURE__*/_jsxs(Flex, {
          justifyContent: "space-between",
          alignItems: "center",
          marginBlockStart: "auto",
          children: [/*#__PURE__*/_jsx(CtaButton, {
            buttonText: buttonText,
            buttonUrl: buttonUrl,
            onClick: onClick,
            openCopilot: openCopilot,
            use: "tertiary-extra-light"
          }), items.length > 0 && /*#__PURE__*/_jsx(CompactPreview, {
            items: items,
            remaining: remaining
          })]
        })]
      })
    })
  });
}